import AddchartIcon from '@mui/icons-material/Addchart';
import MergeIcon from '@mui/icons-material/Merge';
import Checkbox from '@mui/material/Checkbox';
import { InfiniteData } from '@tanstack/query-core';
import {
  useQueryClient,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useCallback, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ButtonPrimary,
  ButtonSecondary,
  FilterByDropdown,
  MobileDropdown,
  ModalStyled,
  SearchInput,
  SortByDropdown,
  ToastStyled,
  TooltipStyled
} from 'src/components';
import { useCurrentUser } from 'src/hooks';
import EmptyCartIcon from 'src/images/icons/empty-cart-icon.png';
import FullCartIcon from 'src/images/icons/full-cart-icon.png';
import { historySearchByAtom } from 'src/store';
import type { CartHistoryData } from 'src/types';
import { baseFetch, exportCartHistoryToExcel } from 'src/utils';
// import { FormatListBulleted as ListViewIcon } from '@mui/icons-material';
// import GridViewIcon from '@mui/icons-material/GridView';

type Props = {
  cartsQuery: UseInfiniteQueryResult<
    InfiniteData<
      {
        carts: CartHistoryData[];
        pageParam: number;
      },
      unknown
    >,
    Error
  >;
  displayedCartsLength: number;
  isListView: boolean;
  setIsListView: React.Dispatch<React.SetStateAction<boolean>>;
  setDebounceDelay: React.Dispatch<React.SetStateAction<number>>;
};

export const CartHistoryHeader = ({
  cartsQuery,
  displayedCartsLength,
  isListView,
  setIsListView,
  setDebounceDelay,
}: Props) => {
  const { user } = useCurrentUser();
  const [searchBy, setSearchBy] = useAtom(historySearchByAtom);
  const [showMergeCartsModal, setShowMergeCartsModal] = useState(false);
  const [mergedCartID, setMergedCartID] = useState<string>('');
  const [selectedCartIDs, setSelectedCartIDs] = useState<string[]>([]);
  const [showMergeCartsSuccessModal, setShowMergeCartsSuccessModal] =
    useState(false);
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const userCartsLength = user!.carts.length;

  const exportCartData = () => {
    if (!cartsQuery.data || !cartsQuery.data.pages) return;

    exportCartHistoryToExcel(cartsQuery.data.pages);
  };

  const handleEnterPress = () => {
    setDebounceDelay(0);
    setTimeout(() => {
      setDebounceDelay(1000);
    }, 20);
  };

  const onClickMergeCarts = useCallback(() => {
    setShowMergeCartsModal(true);
  }, []);

  const handleMergeCarts = useCallback(async () => {
    if (selectedCartIDs.length === 0) return;

    const res = await baseFetch(`/api/merge`, {
      method: 'POST',
      body: JSON.stringify({
        cartIDs: selectedCartIDs,
      }),
    });

    if (!res.ok) {
      const errorData = await res.json();
      toast.custom((t) => (
        <ToastStyled variant='error' handleClose={() => toast.dismiss(t)}>
          {`Failed to merge carts: ${errorData.error}`}
        </ToastStyled>
      ));
      return;
    }

    const data = await res.json();
    setMergedCartID(data.cartid);
    setShowMergeCartsSuccessModal(true);
    setShowMergeCartsModal(false);

    TagManager.dataLayer({
      dataLayer: {
        event: 'Merge Carts',
        eventCategory: 'engagement',
        eventValue: {
          carts: selectedCartIDs.toString(),
        },
      },
    });
    setSelectedCartIDs([]);

    queryClient.invalidateQueries({
      queryKey: ['cart', 'list'],
      exact: false,
      type: 'all',
      refetchType: 'active',
    });
    queryClient.refetchQueries({
      queryKey: ['currentUser'],
    });
  }, [
    selectedCartIDs,
    setShowMergeCartsModal,
    setShowMergeCartsSuccessModal,
    setMergedCartID,
    queryClient,
  ]);

  const handleCloseMergeCartsSuccessModal = useCallback(() => {
    setShowMergeCartsSuccessModal(false);
  }, [setShowMergeCartsSuccessModal]);

  return (
    <div className='grid grid-cols-12 gap-y-4 bg-primary-dark p-6 pt-0 font-secondary xs:pt-2 sm:pt-6 md:px-6 lg:px-8 xl:px-12'>
      <div className='order-1 col-span-8 inline-flex flex-nowrap items-center justify-start gap-2 whitespace-nowrap xs:gap-4'>
        {cartsQuery.isLoading && !cartsQuery.isFetchingNextPage ? (
          <div className='h-[58px] w-[58px] animate-pulse rounded-full bg-slate-600'></div>
        ) : user!.carts && userCartsLength !== 0 ? (
          <img
            src={FullCartIcon}
            alt={t('history.full_cart')}
            className='mb-2 w-[50px]'
          />
        ) : (
          <img
            src={EmptyCartIcon}
            alt={t('history.empty_cart')}
            className='mb-2 w-[50px]'
          />
        )}
        <h1
          className={`font-primary text-2xl font-extrabold text-offWhite xs:text-4xl ${i18n.resolvedLanguage === 'de' || i18n.resolvedLanguage === 'pt' ? 'whitespace-pre-wrap break-all' : ''}`}
        >
          {cartsQuery.isLoading ? (
            <div className='h-6 w-28 animate-pulse rounded bg-slate-600 xs:w-36 sm:w-48'></div>
          ) : (
            t('history.header_carts', { count: userCartsLength })
          )}
        </h1>
      </div>
      <div className='order-2 col-span-4 inline-flex items-center justify-end gap-1 md:hidden'>
        <MobileDropdown isListView={isListView} setIsListView={setIsListView} />
        <TooltipStyled title={t('Merge carts')}>
          <div
            className={`flex items-center justify-center fill-skyBlue active:scale-90 ${displayedCartsLength !== 0
                ? 'cursor-pointer hover:fill-white'
                : 'opacity-20'
              }`}
            onClick={onClickMergeCarts}
          >
            <MergeIcon
              className='cursor-pointer p-0.5 transition-all'
              style={{ fontSize: '29px', fill: 'inherit' }}
            />
          </div>
        </TooltipStyled>
        <TooltipStyled title={t('history.export_history_to_excel')}>
          <div
            className={`flex items-center justify-center fill-skyBlue active:scale-90 ${displayedCartsLength !== 0
                ? 'cursor-pointer hover:fill-white'
                : 'opacity-20'
              }`}
            onClick={exportCartData}
          >
            <AddchartIcon
              className='cursor-pointer p-0.5 transition-all'
              style={{ fontSize: '29px', fill: 'inherit' }}
            />
          </div>
        </TooltipStyled>
      </div>
      <div className='order-3 col-span-12 inline-flex w-full items-center gap-4 lg:order-2 lg:col-span-9'>
        <SearchInput
          value={searchBy}
          onChange={setSearchBy}
          placeholder={t('history.search_carts')}
          focusPlaceholder={t('history.type_to_filter_carts')}
          onEnterPress={handleEnterPress}
          width='w-full max-w-[392px]'
        />
        <div className='hidden gap-4 md:inline-flex'>
          <FilterByDropdown />
          <SortByDropdown />
        </div>
      </div>
      <div className='order-2 col-span-4 hidden items-center justify-end gap-4 md:inline-flex lg:order-3 lg:col-span-3'>
        <TooltipStyled title={t('Merge carts')}>
          <span>
            <button
              className={`inline-flex flex-nowrap items-center justify-center gap-1.5 rounded-full bg-primary-off py-3 pl-[18px] pr-5 font-semibold transition-all active:scale-95 ${displayedCartsLength !== 0
                  ? 'hover:bg-primary-offHover'
                  : 'opacity-20'
                }`}
              disabled={displayedCartsLength === 0}
              onClick={onClickMergeCarts}
            >
              <MergeIcon
                className='p-0.5 text-2xl text-skyBlue'
                style={{ fontSize: '24px' }}
              />
              <span className='text-cloudGray'>{t('Merge')}</span>
            </button>
          </span>
        </TooltipStyled>
        <TooltipStyled title={t('history.export_history_to_excel')}>
          <span>
            <button
              className={`inline-flex flex-nowrap items-center justify-center gap-1.5 rounded-full bg-primary-off py-3 pl-[18px] pr-5 font-semibold transition-all active:scale-95 ${displayedCartsLength !== 0
                  ? 'hover:bg-primary-offHover'
                  : 'opacity-20'
                }`}
              disabled={displayedCartsLength === 0}
              onClick={exportCartData}
            >
              <AddchartIcon
                className='p-0.5 text-2xl text-skyBlue'
                style={{ fontSize: '24px' }}
              />
              <span className='text-cloudGray'>{t('common.export')}</span>
            </button>
          </span>
        </TooltipStyled>
        {/* <ListViewIcon
            className={`cursor-pointer p-0.5 transition-all active:scale-95 ${
              isListView
                ? 'text-white hover:text-skyBlue'
                : 'text-skyBlue hover:text-white'
            }`}
            style={{ fontSize: '30px' }}
            onClick={() => setIsListView(true)}
          /> */}
        {/* <GridViewIcon
            className={`cursor-pointer p-0.5 transition-all active:scale-95 ${
              !isListView
                ? 'text-white hover:text-skyBlue'
                : 'text-skyBlue hover:text-white'
            }`}
            style={{ fontSize: '30px' }}
            onClick={() => setIsListView(false)}
          /> */}
      </div>
      <ModalStyled
        open={showMergeCartsModal}
        onClose={() => setShowMergeCartsModal(false)}
      >
        <div className='flex flex-col items-center justify-center overflow-x-hidden p-4'>
          <h2 className='heading-300 text-balance text-center leading-10'>
            {t('Merge Carts')}
          </h2>
          <p className='text-sm text-gray-500'>
            {t(
              'Merge carts of the same vendor to create a new cart with all the items from the selected carts.',
            )}
          </p>
          <div className='flex w-full flex-col items-center justify-center gap-4 overflow-x-hidden'>
            {cartsQuery.data?.pages.map((page) => (
              <div
                key={page.pageParam}
                className='w-full overflow-x-hidden p-5'
              >
                <div className='grid max-w-full grid-cols-[26px_minmax(0,1fr)_32px_150px_100px] items-center gap-3'>
                  {page.carts.map((cart) => (
                    <div key={cart.cart.id} className='contents'>
                      <div className='flex items-center'>
                        <Checkbox
                          checked={selectedCartIDs.includes(cart.cart.id)}
                          onChange={(e) =>
                            setSelectedCartIDs(
                              e.target.checked
                                ? [...selectedCartIDs, cart.cart.id]
                                : selectedCartIDs.filter(
                                  (id) => id !== cart.cart.id,
                                ),
                            )
                          }
                          className='primary-checkbox'
                        />
                      </div>
                      <div className='line-clamp-2 min-w-0 overflow-hidden'>
                        {cart.cart.title}
                      </div>
                      <div className='flex items-center justify-center'>
                        <img
                          src={`${process.env.REACT_APP_IMG_URL}/images/favis/32/${cart.cart.vendor}.png`}
                          alt={cart.cart.vendor}
                          loading='lazy'
                          className='h-6 w-6'
                        />
                      </div>
                      <div className='min-w-0 overflow-hidden text-ellipsis whitespace-nowrap'>
                        {cart.vendorDisplayName || cart.cart.vendor}
                      </div>
                      <div className='min-w-0 overflow-hidden text-ellipsis whitespace-nowrap'>
                        {cart.cart.id.toUpperCase()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className='flex flex-row items-center justify-center gap-4 p-5'>
            <ButtonPrimary onClick={handleMergeCarts} customHeight='h-12'>
              {t('Merge')}
            </ButtonPrimary>
            <ButtonSecondary
              onClick={() => setShowMergeCartsModal(false)}
              customHeight='h-12'
            >
              {t('Cancel')}
            </ButtonSecondary>
          </div>
        </div>
      </ModalStyled>
      <ModalStyled
        open={showMergeCartsSuccessModal}
        onClose={handleCloseMergeCartsSuccessModal}
      >
        <div className='flex flex-col items-center justify-center p-4'>
          <h2 className='heading-300 text-balance text-center leading-10'>
            {t('Carts merged!')}
          </h2>
          <p className='text-sm text-gray-500'>
            {t(
              'The carts have been merged successfully, you can now view the merged cart in your cart history.',
            )}
          </p>
          <p className='text-center text-sm text-primary-dark'>
            {t('The merged cart ID is')}:
          </p>
          <h3 className='heading-400 text-balance text-center leading-10'>
            {mergedCartID}
          </h3>
          <div className='flex flex-row items-center justify-center gap-4 p-5'>
            <ButtonPrimary
              onClick={handleCloseMergeCartsSuccessModal}
              customHeight='h-12'
            >
              {t('Close')}
            </ButtonPrimary>
          </div>
        </div>
      </ModalStyled>
    </div>
  );
};
